import type {
  APIFormatThemeButtonData,
  APIFormatThemeLayoutData,
  APIFormatThemeTypographyData,
  APILayoutResponsivePadding,
  AppBannerFormData,
} from "containers/admin/clients/client/theme-page/preview/preview.types";

export { FALLBACK_FONT_FAMILY_OPTIONS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/typography/typography.constants";
export { APP_WEIGHT_LABELS as FONT_WEIGHT_LABELS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/fonts.constants";

export const EMAIL_CONFIGURATION_TABS = [
  { value: "typography", label: "Typography" },
  { value: "button", label: "Button" },
  { value: "banner", label: "Banner" },
  { value: "layout", label: "Layout" },
] as const;

export const EMAIL_CONFIGURATION_FORM_ID = "email-configuration-form";

export const TYPOGRAPHY_SECTIONS = [
  { name: "heading", title: "Heading (H1)", colorLabel: "H1 color" },
  { name: "subHeading", title: "Subheading (H2)", colorLabel: "H2 color" },
  { name: "paragraph", title: "Paragraph", colorLabel: "Paragraph color" },
  { name: "buttonLabel", title: "Button label", colorLabel: "Button label color" },
] as const;

export const FONT_SIZE_OPTIONS = ["12", "14", "16", "18", "20", "24", "30", "36", "48", "60", "72", "96", "128"].map(
  (size) => ({ label: `${size}px`, value: `${size}px` }),
);

export const LINE_HEIGHT_OPTIONS = ["1.2", "1.5", "1.8", "2.0"].map((value) => ({ label: value, value }));

export const FONT_WEIGHTS = ["100", "200", "300", "400", "500", "600", "700", "800", "900"];

export const BUTTON_WIDTH_MODES = [
  { value: "auto", label: "Hug content" },
  { value: "fill", label: "Fill container" },
  { value: "fixed", label: "Fixed width" },
] as const;

/** The legacy theme saves a fill-container button as `100` and a fixed one as its percentage. */
export const FILL_BUTTON_WIDTH = "100";

export const DEFAULT_FIXED_BUTTON_WIDTH = "50";

/**
 * BEE's own button width control: no narrower than 20%, in steps of 5% — a value between steps is rounded
 * away inside the editor, so the theme would no longer match what BEE shows. Not in BEE's docs; taken from
 * the editor and the legacy theme page, which enforces the same.
 */
export const MIN_FIXED_BUTTON_WIDTH = 20;

export const FIXED_BUTTON_WIDTH_STEP = 5;

/** 100% is saved the same as Fill container and would read back as it, so a fixed width stops a step short. */
export const MAX_FIXED_BUTTON_WIDTH = 95;

export const BORDER_RADIUS_OPTIONS = [
  { label: "0", value: "0" },
  { label: "2px", value: "2px" },
  { label: "4px", value: "4px" },
  { label: "6px", value: "6px" },
  { label: "8px", value: "8px" },
  { label: "12px", value: "12px" },
  { label: "16px", value: "16px" },
  { label: "Rounded", value: "60px" },
];

export const BORDER_STYLE_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Solid", value: "solid" },
  { label: "Dashed", value: "dashed" },
  { label: "Dotted", value: "dotted" },
];

export const CONTENT_BLOCK_GROUPS = [
  { value: "all-blocks", label: "All content blocks" },
  { value: "custom", label: "Custom" },
] as const;

export const CONTENT_BLOCKS = [
  { name: "headings", title: "Headings" },
  { name: "paragraph", title: "Paragraph" },
  { name: "button", title: "Button" },
] as const;

/** The legacy padding inputs' limits. */
export const MAX_PADDING = 60;

export const BANNER_MAX_FILE_SIZE = 256 * 1024;

export const BANNER_ACCEPT = "image/png, image/jpeg, image/gif";

export const BANNER_SPECIFICATIONS = [
  { label: "Format:", value: "PNG | JPEG | GIF" },
  { label: "Maximum file size:", value: "256 KB" },
  { label: "Recommended dimensions:", value: "Ensure the width is 500 pixels." },
];

export const COLOR_NONE_VALUE = "none";

export const COLOR_CUSTOM_VALUE = "custom";

const BLOCK_PADDING: APILayoutResponsivePadding = {
  mobile: { left: "10", right: "10", top: "10", bottom: "10" },
  desktop: { left: "10", right: "10", top: "10", bottom: "10" },
};

/**
 * The legacy page's starting theme (`theme-default-data.json`) in the saved format, for a client whose
 * theme lacks a section. Colour keys resolve against the legacy colours, as they always have.
 */
export const DEFAULT_EMAIL_THEME: {
  typography: APIFormatThemeTypographyData;
  button: APIFormatThemeButtonData;
  banner: AppBannerFormData;
  layout: APIFormatThemeLayoutData;
} = {
  typography: {
    heading: {
      fontFamily: "",
      fallbackFontFamily: "",
      fontSize: "24px",
      lineHeight: "1.2",
      fontWeight: "700",
      textColor: "intense",
      alignment: "left",
    },
    subHeading: {
      fontFamily: "",
      fallbackFontFamily: "",
      fontSize: "20px",
      lineHeight: "1.2",
      fontWeight: "600",
      textColor: "intense",
      alignment: "left",
    },
    paragraph: {
      fontFamily: "",
      fallbackFontFamily: "",
      fontSize: "16px",
      lineHeight: "1.2",
      fontWeight: "400",
      textColor: "bold",
      alignment: "left",
      linkColor: "bold",
    },
    buttonLabel: {
      fontFamily: "",
      fallbackFontFamily: "",
      fontSize: "16px",
      lineHeight: "1.2",
      fontWeight: "700",
      textColor: "white",
    },
  },
  button: {
    padding: { left: "16", right: "16", top: "10", bottom: "10" },
    width: "auto",
    alignment: "left",
    backgroundColor: "soft",
    border: { radius: "4px", style: "none", width: "1", color: "soft" },
  },
  banner: { imageUrl: "", fileName: "" },
  layout: {
    contentArea: { left: "0", right: "0", top: "5", bottom: "5" },
    contentBlock: {
      group: "all-blocks",
      allBlocks: BLOCK_PADDING,
      headings: BLOCK_PADDING,
      paragraph: BLOCK_PADDING,
      button: BLOCK_PADDING,
    },
  },
};
